import { motion } from 'framer-motion';
import { CheckCircle2, Award, Users, Clock, Shield, TrendingUp, Globe2, HeartHandshake, ArrowRight } from 'lucide-react';
import { Link } from 'react-router-dom';

const WhyChooseUs = () => {
  const reasons = [
    {
      icon: <Award className="w-10 h-10" />,
      title: 'Proven Expertise',
      description: 'Years of experience in UAE business setup and relocation services with a track record of successful projects.',
      color: '#3498DB'
    },
    {
      icon: <Users className="w-10 h-10" />,
      title: 'Expert Team',
      description: 'Seasoned professionals combining local knowledge with international experience.',
      color: '#27AE60'
    },
    {
      icon: <Globe2 className="w-10 h-10" />,
      title: '360° Approach',
      description: 'Comprehensive end-to-end solutions covering every aspect of your journey.',
      color: '#5DADE2'
    },
    {
      icon: <Clock className="w-10 h-10" />,
      title: 'Fast Processing',
      description: 'Efficient processes ensuring quick turnaround times without compromising quality.',
      color: '#D4AF37'
    },
    {
      icon: <Shield className="w-10 h-10" />,
      title: 'Compliance Assurance',
      description: 'Stay fully compliant with UAE regulations through our expert guidance.',
      color: '#E74C3C'
    },
    {
      icon: <TrendingUp className="w-10 h-10" />,
      title: 'Tailored Solutions',
      description: 'Personalized approach ensuring solutions that align with your specific goals.',
      color: '#9B59B6'
    },
    {
      icon: <HeartHandshake className="w-10 h-10" />,
      title: 'Client-Centric Focus',
      description: 'Long-term relationships based on trust, transparency, and exceptional service.',
      color: '#1ABC9C'
    },
    {
      icon: <CheckCircle2 className="w-10 h-10" />,
      title: 'Post-Setup Support',
      description: 'Ongoing support to help you navigate challenges and seize opportunities.',
      color: '#F39C12'
    },
  ];

  const stats = [
    { number: '15+', label: 'Years Experience', color: '#3498DB' },
    { number: '98%', label: 'Client Satisfaction', color: '#27AE60' },
    { number: '6', label: 'Countries (Bahrain, Seychelles, Mauritius, Hong Kong, Panama, Dubai)', color: '#5DADE2' },
  ];

  const testimonials = [
    {
      quote: 'SAM Relocation Group made our move to Dubai seamless. Their team handled everything from visas to finding the perfect home. Highly recommended!',
      author: 'Michael Chen',
      company: 'Tech Entrepreneur',
    },
    {
      quote: 'The expertise and professionalism of the SAM team were outstanding. They guided us through every step of setting up our business.',
      author: 'Sarah Williams',
      company: 'E-commerce Business Owner',
    },
    {
      quote: 'Their tax and compliance consulting saved us significant costs while ensuring full regulatory compliance. True experts.',
      author: 'Robert Anderson',
      company: 'International Investor',
    },
  ];

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: { opacity: 1, transition: { staggerChildren: 0.1 } }
  };

  const itemVariants = {
    hidden: { opacity: 0, y: 40 },
    visible: { opacity: 1, y: 0, transition: { duration: 0.8, ease: [0.25, 0.1, 0.25, 1] } }
  };

  return (
    <div className="min-h-screen bg-[#0a0a0a]" data-testid="why-choose-us-page">
      {/* Hero Section */}
      <section className="relative min-h-[60vh] flex items-center justify-center text-white pt-20" data-testid="why-choose-us-hero">
        <div className="absolute inset-0">
          <img 
            src="https://images.unsplash.com/photo-1512453979798-5ea266f8880c?w=1920" 
            alt="Dubai"
            className="w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-[#0a0a0a] via-[#0a0a0a]/70 to-[#0a0a0a]" />
        </div>
        
        <div className="relative z-10 max-w-7xl mx-auto px-6 text-center">
          <motion.div
            initial={{ opacity: 0, y: 40 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 1 }}
          >
            <div className="flex items-center justify-center gap-4 mb-6">
              <div className="w-20 h-[1px] bg-gradient-to-r from-transparent to-[#D4AF37]" />
              <span className="text-[#D4AF37] font-semibold tracking-[0.2em] uppercase text-sm">Why Us</span>
              <div className="w-20 h-[1px] bg-gradient-to-l from-transparent to-[#D4AF37]" />
            </div>
            
            <h1 className="text-5xl md:text-7xl font-bold mb-6" data-testid="why-choose-us-hero-title">
              Why Choose <span className="text-transparent bg-clip-text bg-gradient-to-r from-[#D4AF37] to-[#27AE60]">SAM</span>?
            </h1>
            <p className="text-xl text-gray-300 max-w-3xl mx-auto">
              Your trusted partner for business success in the UAE
            </p>
          </motion.div>
        </div>
      </section>

      {/* Stats Section */}
      <section className="py-16 bg-[#0a0a0a] border-y border-white/10" data-testid="stats-section">
        <div className="max-w-7xl mx-auto px-6">
          <motion.div 
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            variants={containerVariants}
            className="grid grid-cols-3 gap-8"
          >
            {stats.map((stat, index) => (
              <motion.div
                key={index}
                variants={itemVariants}
                className="text-center"
                data-testid={`stat-${index}`}
              >
                <motion.div 
                  className="text-5xl md:text-6xl font-bold mb-2"
                  style={{ color: stat.color }}
                  whileHover={{ scale: 1.1 }}
                >
                  {stat.number}
                </motion.div>
                <div className="text-gray-400">{stat.label}</div>
              </motion.div>
            ))}
          </motion.div>
        </div>
      </section>

      {/* Reasons Section */}
      <section className="py-24 bg-[#0a0a0a]" data-testid="reasons-section">
        <div className="max-w-7xl mx-auto px-6">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <h2 className="text-4xl md:text-5xl font-bold text-white mb-4" data-testid="reasons-heading">
              What Sets Us <span className="text-transparent bg-clip-text bg-gradient-to-r from-[#3498DB] to-[#27AE60]">Apart</span>
            </h2>
            <p className="text-lg text-gray-400 max-w-2xl mx-auto">
              Discover the advantages of partnering with SAM Relocation Group
            </p>
          </motion.div>

          <motion.div 
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            variants={containerVariants}
            className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6"
          >
            {reasons.map((reason, index) => (
              <motion.div
                key={index}
                variants={itemVariants}
                className="group relative overflow-hidden rounded-3xl bg-gradient-to-br from-white/5 to-transparent border border-white/10 p-8 text-center hover:border-white/20 transition-all duration-500"
                data-testid={`reason-card-${index}`}
              >
                <motion.div 
                  className="w-20 h-20 mx-auto rounded-2xl flex items-center justify-center mb-6"
                  style={{ background: `${reason.color}15`, color: reason.color }}
                  whileHover={{ scale: 1.1, rotate: 5 }}
                >
                  {reason.icon}
                </motion.div>
                <h3 className="text-lg font-bold mb-3 text-white">{reason.title}</h3>
                <p className="text-gray-400 text-sm leading-relaxed">{reason.description}</p>
                
                <div 
                  className="absolute bottom-0 left-0 h-1 w-0 group-hover:w-full transition-all duration-500"
                  style={{ background: `linear-gradient(90deg, ${reason.color}, transparent)` }}
                />
              </motion.div>
            ))}
          </motion.div>
        </div>
      </section>

      {/* Testimonials */}
      <section className="py-24 bg-gradient-to-b from-[#0a0a0a] to-[#111]" data-testid="testimonials-section">
        <div className="max-w-7xl mx-auto px-6">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <h2 className="text-4xl md:text-5xl font-bold text-white mb-4" data-testid="testimonials-heading">
              Client <span className="text-transparent bg-clip-text bg-gradient-to-r from-[#D4AF37] to-[#F4D03F]">Success Stories</span>
            </h2>
          </motion.div>

          <motion.div 
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            variants={containerVariants}
            className="grid grid-cols-1 md:grid-cols-3 gap-8"
          >
            {testimonials.map((testimonial, index) => (
              <motion.div
                key={index}
                variants={itemVariants}
                className="relative overflow-hidden rounded-3xl bg-gradient-to-br from-white/5 to-transparent border border-white/10 p-10 hover:border-[#D4AF37]/30 transition-all duration-500"
                data-testid={`testimonial-${index}`}
              >
                <div className="text-7xl font-serif text-[#D4AF37]/20 absolute -top-2 -left-2">"</div>
                <p className="text-gray-300 leading-relaxed mb-8 relative z-10">{testimonial.quote}</p>
                <div className="border-t border-white/10 pt-6">
                  <p className="font-bold text-white">{testimonial.author}</p>
                  <p className="text-[#D4AF37] text-sm">{testimonial.company}</p>
                </div>
              </motion.div>
            ))}
          </motion.div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-24 relative overflow-hidden" data-testid="why-choose-us-cta">
        <div className="absolute inset-0">
          <img 
            src="https://images.unsplash.com/photo-1518684079-3c830dcef090?w=1920" 
            alt="Dubai Night"
            className="w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-[#0a0a0a]/85" />
        </div>
        
        <div className="max-w-4xl mx-auto px-6 text-center relative z-10">
          <motion.div
            initial={{ opacity: 0, y: 40 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8 }}
          >
            <h2 className="text-4xl md:text-5xl font-bold text-white mb-6" data-testid="cta-heading">
              Experience the <span className="text-transparent bg-clip-text bg-gradient-to-r from-[#D4AF37] to-[#27AE60]">SAM Difference</span>
            </h2>
            <p className="text-xl text-gray-300 mb-10 leading-relaxed">
              Join satisfied clients who have achieved their goals in the UAE with our expert support.
            </p>
            <Link 
              to="/contact" 
              className="inline-flex items-center gap-3 px-10 py-5 bg-gradient-to-r from-[#D4AF37] to-[#27AE60] text-white font-bold text-lg rounded-full hover:shadow-[0_0_40px_rgba(212,175,55,0.5)] transition-all duration-300"
              data-testid="cta-button"
            >
              Get Started Today
              <ArrowRight size={22} />
            </Link>
          </motion.div>
        </div>
      </section>
    </div>
  );
};

export default WhyChooseUs;
